import clsx from 'clsx';

import { useBranding } from '../context/BrandingContext';

interface LogoProps {
  /** Pixel size of the gradient mark. Default 32 (nav / login). */
  size?: number;
  /** Render the "DocuSense" wordmark next to the mark. Default true. */
  showWordmark?: boolean;
  /** Wordmark colour: `ink` (default, for light surfaces) or `light` (dark panes). */
  tone?: 'ink' | 'light';
  className?: string;
}

/**
 * DocuSense brand mark — the gradient rounded square with the `</>` glyph used
 * across the marketing landing, plus the JetBrains Mono wordmark. Single source
 * of truth so the panel and the landing stay visually identical; the gradient
 * itself lives in `.brand-mark` (global.css) to mirror the landing's `.brand .mark`.
 */
export function Logo({ size = 32, showWordmark = true, tone = 'ink', className }: LogoProps): JSX.Element {
  // White-label (M1): a tenant logo URL replaces BOTH the gradient mark and the wordmark — a logo
  // usually already carries the company name. Falls back to the DocuSense baseline (gradient mark +
  // wordmark) when no branding is set.
  const { productName, logoUrl, logoHeightPx } = useBranding();

  if (logoUrl !== null) {
    // Render at the configured height with auto width (aspect preserved) and a max-width cap so a
    // very wide logo can't blow out the header. No square crop — the logo keeps its own shape.
    return (
      <span className={clsx('flex select-none items-center', className)}>
        <img
          src={logoUrl}
          alt={productName}
          className="max-w-[220px] flex-none object-contain"
          style={{ height: logoHeightPx, width: 'auto' }}
        />
      </span>
    );
  }

  return (
    <span className={clsx('flex select-none items-center gap-2.5', className)}>
      <span
        aria-hidden
        className="brand-mark grid flex-none place-items-center rounded-[28%] font-mono font-bold leading-none text-white"
        style={{ width: size, height: size, fontSize: Math.round(size * 0.34) }}
      >
        &lt;/&gt;
      </span>
      {showWordmark ? (
        <span
          className={clsx(
            'font-display font-bold tracking-tight',
            tone === 'light' ? 'text-white' : 'text-ink',
          )}
          style={{ fontSize: Math.round(size * 0.6) }}
        >
          {productName}
        </span>
      ) : null}
    </span>
  );
}
